import type { Practitioner, Reference } from '@medplum/fhirtypes'
import { invariant } from '@seen/core/invariant'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { VisitNote } from '@seen/core/models/visitNote'
import {
  getFilenameFromContentDisposition,
  isOfType,
  saveBlobWithName,
} from '@seen/core/utils'
import { TypographyPageTitle } from '@seen/ui'
import {
  IconBuildingCommunity,
  IconBuildingHospital,
  IconCalendar,
  IconCheck,
  IconFileDescription,
  IconFileExport,
  IconNotes,
  IconPhone,
  IconPill,
  IconReport,
  IconStethoscope,
  IconTimelineEventText,
  IconTool,
} from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import { compact } from 'lodash-es'
import { type JSX, useContext, useLayoutEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router'
import { toast } from 'sonner'
import { match } from 'ts-pattern'
import { CommentComposer } from '#src/components/CommentComposer.tsx'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { VirtualizedList } from '#src/components/core/VirtualizedList.tsx'
import type { Filter } from '#src/components/filter/Filter.tsx'
import { FilterComposer } from '#src/components/filter/FilterComposer.tsx'
import { useFilteredData } from '#src/components/filter/useFilteredData.ts'
import { MoreDropdownMenu } from '#src/components/MoreDropdownMenu.tsx'
import { PractitionerOption } from '#src/components/options/PractitionerOption.tsx'
import { useCurrentParticipant } from '#src/contexts/current-participant/useCurrentParticipant.ts'
import { LayoutModeContext } from '#src/contexts/LayoutModeContext.ts'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import { useCurrentPractitionerPerms } from '#src/hooks/useCurrentPractitionerPerms.ts'
import { usePractitionersFrom } from '#src/hooks/usePractitionersFrom.ts'
import { useRead } from '#src/hooks/useRead.ts'
import {
  type TimelineItem,
  useTimelineItems,
} from '#src/hooks/useTimelineItems.ts'
import { getTimelineItemIdFromUrl } from '#src/hooks/useTimelineItemUrl.ts'
import {
  estimateTimelineRowHeight,
  useTimelineListItems,
} from '#src/hooks/useTimelineListItems.ts'
import { TimelineListRow } from '#src/pages/participants/timeline/TimelineListRow.tsx'

const AUDITOR_VISIBLE_TIMELINE_ITEM_TYPES: TimelineItem['type'][] = [
  'validatedTool',
  'visitNote',
  'assessment',
  'timelineNote',
  'appointment',
  'medicationRequest',
  'order',
  'task',
  'documentReference',
  'groupedDocumentReferences',
  'dayCenterDayNote',
  'facilityEncounter',
  'recurringAppointment',
  'fax',
]

/**
 * Extract the author reference from a timeline item.
 * This function handles all timeline item types that might contain author information
 * and returns a standardized reference string if available.
 * @param item - The timeline item to extract the author reference from
 * @returns A reference to a Practitioner, or undefined if not found
 */
function getAuthorRef(item: TimelineItem): Reference<Practitioner> | undefined {
  return match(item)
    .with({ type: 'validatedTool' }, (v) => v.data.author)
    .with({ type: 'visitNote' }, (v) => {
      const author =
        v.data instanceof VisitNote ? v.data.author?.[0] : v.data.owner
      if (isOfType(author, 'Practitioner')) {
        return author
      }
      return undefined
    })
    .with({ type: 'assessment' }, (v) => v.data.author)
    .with({ type: 'timelineNote' }, (v) => {
      return v.data.practitionerSender
    })
    .with({ type: 'phoneCall' }, (v) => {
      return v.data.practitionerSender
    })
    .with({ type: 'fax' }, (v) => {
      if (v.data.practitionerSender) {
        return v.data.practitionerSender
      }
      if (isOfType(v.data.recipient, 'Practitioner')) {
        return v.data.recipient
      }
      return undefined
    })
    .with({ type: 'task' }, (v) => {
      // TODO: support organization owners
      return v.data.ownerPractitioner
    })
    .with({ type: 'order' }, (v) => v.data.requester)
    .with({ type: 'documentReference' }, (v) => {
      const author = v.data.author
      if (isOfType(author, 'Practitioner')) {
        return author
      }
      return undefined
    })
    .with({ type: 'groupedDocumentReferences' }, (v) => {
      const author = v.data[0]?.author
      if (isOfType(author, 'Practitioner')) {
        return author
      }
      return undefined
    })
    .with({ type: 'dayCenterDayNote' }, (v) => v.data.performerPractitioner)
    .with({ type: 'facilityEncounter' }, (v) => v.data.practitioner)
    .with(
      { type: 'recurringAppointment' },
      (v) => v.data?.[0].practitioners?.[0]
    )
    .with({ type: 'appointment' }, (v) => v.data.practitioners?.[0])
    .with({ type: 'medicationRequest' }, (v) => {
      if (isOfType(v.data.requester, 'Practitioner')) {
        return v.data.requester
      }
      return undefined
    })
    .exhaustive()
}

export function ParticipantTimelinePage(): JSX.Element {
  const { patientId } = useParams()
  invariant(patientId, 'Patient ID is required')
  return <ParticipantTimelinePageComponent key={patientId} />
}

function ParticipantTimelinePageComponent(): JSX.Element {
  const participant = useCurrentParticipant()
  const { isAuditor } = useCurrentPractitionerPerms()

  const setLayoutMode = useContext(LayoutModeContext)
  useLayoutEffect(() => {
    setLayoutMode?.('full')
    return () => setLayoutMode?.('auto')
  }, [setLayoutMode])

  const location = useLocation()

  /* Extract timeline-item ID from URL hash for deep-linking. Accepts both the
     canonical #item-<id> and the legacy #comment-<id>, and strips anything
     after a second `#` (e.g. `#item-aaaa#comment-bbbb` → `aaaa`). */
  const hashItemId = useMemo(
    () => getTimelineItemIdFromUrl(location.hash),
    [location.hash]
  )

  /* Fetch as CommentCommunication to detect the reply case so we can scroll
     to the parent's row. For non-comment resources the fetch returns no useful
     parentComment and we fall through to ID-suffix matching against row keys. */
  const { data: linkedComment } = useRead(CommentCommunication, hashItemId)

  const {
    timelineItems: unfilteredItems,
    isLoading,
    isEmpty,
  } = useTimelineItems(participant.reference, {
    includeTypes: isAuditor ? AUDITOR_VISIBLE_TIMELINE_ITEM_TYPES : undefined,
  })

  const seenApi = useSeenApi().seenApi
  const exportTimelineMutation = useMutation({
    mutationFn: async () => {
      const response = await seenApi.post(
        `/pdf/timeline/${participant.id}`,
        {},
        { responseType: 'blob' }
      )
      const filename = getFilenameFromContentDisposition(
        response.headers['content-disposition']
      )
      saveBlobWithName(response.data, filename ?? 'timeline.pdf')
    },
    onMutate: () => {
      toast.loading('Exporting timeline…', {
        id: `export-timeline-${participant.id}`,
      })
    },
    onSettled: () => {
      toast.dismiss(`export-timeline-${participant.id}`)
    },
    onError: () => {
      toast.error('Failed to export timeline')
    },
  })

  const authors = usePractitionersFrom(
    useMemo(() => compact(unfilteredItems.map(getAuthorRef)), [unfilteredItems])
  )
  const practitionerOptions = useMemo(
    () =>
      authors.map((author) => ({
        value: author.reference.reference,
        icon: (
          <Avatar profile={author.reference} size="icon" className="size-4" />
        ),
        label: <PractitionerOption practitioner={author.reference} />,
        keywords: author.searchKeywords,
      })),
    [authors]
  )

  const filterOptions = useMemo(
    () => ({
      type: {
        label: 'Type',
        type: 'multi-select' as const,
        options: [
          {
            value: 'validatedTool',
            icon: <IconTool className="size-3.5" />,
            label: (
              <div className="flex items-center gap-2">
                <IconTool className="size-3.5" />
                Validated Tool
              </div>
            ),
            keywords: ['validated tool'],
          },
          {
            value: 'visitNote',
            icon: <IconNotes className="size-3.5" />,
            label: (
              <div className="flex items-center gap-2">
                <IconNotes className="size-3.5" />
                Visit Note
              </div>
            ),
            keywords: ['visit note'],
          },
          {
            value: 'assessment',
            icon: <IconReport className="size-3.5" />,
            label: (
              <div className="flex items-center gap-2">
                <IconReport className="size-3.5" />
                Assessment
              </div>
            ),
            keywords: ['assessment'],
          },
          {
            value: 'timelineNote',
            icon: <IconTimelineEventText className="size-3.5" />,
            label: (
              <div className="flex items-center gap-2">
                <IconTimelineEventText className="size-3.5 shrink-0" />
                Timeline Note
              </div>
            ),
            keywords: ['timeline note'],
          },
          ...(!isAuditor
            ? [
                {
                  value: 'phoneCall',
                  icon: <IconPhone className="size-3.5" />,
                  label: (
                    <div className="flex items-center gap-2">
                      <IconPhone className="size-3.5" />
                      Phone Call
                    </div>
                  ),
                  keywords: ['phone call'],
                },
              ]
            : []),
          {
            value: 'appointment',
            icon: <IconCalendar className="size-3.5" />,
            label: (
              <div className="flex items-center gap-2">
                <IconCalendar className="size-3.5" />
                Appointment
              </div>
            ),
            keywords: ['appointment', 'recurring appointment'],
          },
          {
            value: 'medicationRequest',
            icon: <IconPill className="size-3.5" />,
            label: (
              <div className="flex items-center gap-2">
                <IconPill className="size-3.5" />
                Medication
              </div>
            ),
            keywords: ['medication'],
          },
          {
            value: 'order',
            icon: <IconStethoscope className="size-3.5" />,
            label: (
              <div className="flex items-center gap-2">
                <IconStethoscope className="size-3.5" />
                Order
              </div>
            ),
            keywords: ['order'],
          },
          {
            value: 'task',
            icon: <IconCheck className="size-3.5" />,
            label: (
              <div className="flex items-center gap-2">
                <IconCheck className="size-3.5" />
                Task
              </div>
            ),
            keywords: ['task'],
          },
          {
            value: 'documentReference',
            icon: <IconFileDescription className="size-3.5" />,
            label: (
              <div className="flex items-center gap-2">
                <IconFileDescription className="size-3.5" />
                Document
              </div>
            ),
            keywords: ['document', 'file'],
          },
          {
            value: 'dayCenterDayNote',
            icon: <IconBuildingCommunity className="size-3.5" />,
            label: (
              <div className="flex items-center gap-2">
                <IconBuildingCommunity className="size-3.5 shrink-0" />
                Day Center Note
              </div>
            ),
            keywords: ['day center', 'note'],
          },
          {
            value: 'facilityEncounter',
            icon: <IconBuildingHospital className="size-3.5" />,
            label: (
              <div className="flex items-center gap-2">
                <IconBuildingHospital className="size-3.5" />
                Facility Encounter
              </div>
            ),
            keywords: ['facility', 'encounter'],
          },
        ],
        valueFn: (item: TimelineItem) =>
          item.type === 'groupedDocumentReferences'
            ? 'documentReference'
            : item.type,
      },
      author: {
        label: 'Practitioner',
        type: 'multi-select' as const,
        options: practitionerOptions,
        valueFn: (item: TimelineItem) => getAuthorRef(item)?.reference ?? '',
      },
    }),
    [isAuditor, practitionerOptions]
  )

  const [filters, setFilters] = useState<Filter<keyof typeof filterOptions>[]>(
    []
  )

  const filteredItems = useFilteredData(unfilteredItems, filterOptions, filters)

  const timelineListItems = useTimelineListItems(filteredItems)
  const rows = useMemo(
    () =>
      timelineListItems.map((item) => (
        <div key={item.key} className="px-pagex-default py-2 lg:px-pagex-lg">
          <TimelineListRow item={item} />
        </div>
      )),
    [timelineListItems]
  )

  /* Resolve the row index to scroll to. Comment replies are nested under
     their root comment's row, so we follow parentComment in that case.
     For everything else (including non-comment items like faxes), the row
     key ends in `/{id}` — match on that suffix. */
  const initialScrollToIndex = useMemo(() => {
    if (!hashItemId) {
      return undefined
    }
    if (linkedComment?.parentComment?.reference) {
      const idx = timelineListItems.findIndex(
        (item) => item.key === linkedComment.parentComment?.reference
      )
      if (idx !== -1) {
        return idx
      }
    }
    const suffix = `/${hashItemId}`
    const idx = timelineListItems.findIndex((item) => item.key.endsWith(suffix))
    return idx === -1 ? undefined : idx
  }, [linkedComment, hashItemId, timelineListItems])

  if (isLoading || !participant) {
    return <Loader />
  }

  return (
    <div className="relative flex size-full flex-col gap-4 pt-2.5 lg:pt-2">
      <div className="flex flex-col gap-4 px-pagex-default lg:px-pagex-lg">
        <div className="flex items-center justify-between">
          <TypographyPageTitle>Timeline</TypographyPageTitle>
          <MoreDropdownMenu>
            <DropdownMenuItem
              onSelect={() => exportTimelineMutation.mutate()}
              disabled={exportTimelineMutation.isPending}
            >
              <IconFileExport className="size-4" />
              {exportTimelineMutation.isPending
                ? 'Exporting…'
                : 'Export Timeline'}
            </DropdownMenuItem>
          </MoreDropdownMenu>
        </div>
        <div className="flex flex-col gap-4">
          <div
            className="-mx-1.5" // Align with the timeline items.
          >
            <CommentComposer
              partOf={[participant.reference]}
              subject={participant.reference}
              placeholder={`Leave a comment on ${participant.firstNamePossessive} timeline…`}
            />
          </div>
          <FilterComposer
            id="participant-timeline-filters"
            filterOptions={filterOptions}
            onChange={setFilters}
          />
        </div>
      </div>
      {isEmpty && (
        <p className="px-pagex-default text-foreground-secondary lg:px-pagex-lg">
          There is no activity for {participant.name}.
        </p>
      )}
      {!isEmpty && (
        <div
          aria-label="Participant timeline"
          role="feed"
          className="min-h-0 flex-1"
        >
          <VirtualizedList
            rows={rows}
            estimateSize={estimateTimelineRowHeight}
            scrollRestorationId={`timeline-list-${participant.reference.reference}`}
            dynamic
            initialScrollToIndex={initialScrollToIndex}
          />
        </div>
      )}
    </div>
  )
}
